'use client';

import Link from 'next/link';
import { ArrowUpRight } from 'lucide-react';
import { useCatalog } from '../hooks/use-billing';

interface WidgetTeaserProps {
  /** Codul modulului din catalog (ex. 'stock', 'invoicing_pro') */
  moduleCode: string;
  /** O propoziție scurtă care descrie ce ar afișa widget-ul (override) */
  hint?: string;
}

/**
 * Teaser inline pentru widget-uri de pe dashboard care depind de un modul plătit.
 *
 * Se randează ÎN INTERIORUL `WidgetCard` (care furnizează border + bg + header
 * cu titlul widget-ului). Componenta nu adaugă propriul card — doar conținutul:
 *   • un blob atmospheric primary în colț (în loc de border vizibil)
 *   • un mic badge JetBrains Mono cu eticheta „Modul Premium"
 *   • numele modulului în Fraunces serif, cu ultimul cuvânt italic primary
 *   • o linie decorativă cu dot accent (semnătura discretă)
 *   • un hint scurt
 *   • CTA „Vezi plan ↗" cu underline dotted → solid pe hover
 *
 * Estetică „editorial whisper" — coerent cu paywall-ul mare de pe `/stocuri`.
 */
export function WidgetTeaser({ moduleCode, hint }: WidgetTeaserProps) {
  const { data: catalog } = useCatalog();
  const moduleDef = (catalog as any)?.modules?.find((m: any) => m.code === moduleCode);
  const moduleName: string = moduleDef?.name || 'Modul Premium';
  const moduleDescription: string | undefined = moduleDef?.description;
  const displayHint = hint || (moduleDescription ? moduleDescription + '.' : undefined);

  // Split numele modulului — ultimul cuvânt devine accent italic primary, ca în
  // paywall-ul mare. „Facturare PRO" → „Facturare" + italic „PRO". „Gestiune
  // Stoc" → „Gestiune" + italic „Stoc". O singură vorbă rămâne integral italic.
  const parts = moduleName.trim().split(' ');
  const baseName = parts.length > 1 ? parts.slice(0, -1).join(' ') : '';
  const accentName = parts.length > 1 ? parts[parts.length - 1] : moduleName;

  return (
    <div className="group/teaser relative flex h-full flex-col">
      {/* Blob atmospheric primary — colț dreapta sus, blur subtle */}
      <div
        aria-hidden
        className="pointer-events-none absolute -top-4 -right-4 h-32 w-32 rounded-full opacity-[0.09] blur-2xl transition-opacity duration-500 group-hover/teaser:opacity-[0.18]"
        style={{ background: 'rgb(var(--primary-500))' }}
      />

      {/* Badge mic — vocabular tipografic identic cu paywall-ul mare */}
      <span
        className="relative self-start inline-flex items-center gap-1.5 rounded-md border border-primary/25 bg-primary/[0.06] px-2 py-[3px] text-[0.6rem] font-semibold tracking-[0.18em] uppercase text-primary"
        style={{ fontFamily: 'JetBrains Mono, ui-monospace, monospace' }}
      >
        <span className="inline-block h-1 w-1 rounded-full bg-primary" />
        Modul Premium
      </span>

      {/* Titlul = numele modulului, în Fraunces, ca în paywall */}
      <h3
        className="relative mt-3 text-[1.45rem] leading-[1.05] font-medium tracking-[-0.015em] text-neutral-900 dark:text-neutral-100"
        style={{ fontFamily: 'Fraunces, Georgia, serif' }}
      >
        {baseName && <>{baseName} </>}
        <span className="italic" style={{ color: 'rgb(var(--primary-500))' }}>
          {accentName}
        </span>
      </h3>

      {/* Linie decorativă subtle + dot accent care scalează pe hover */}
      <div className="relative mt-3 mb-2.5 flex items-center gap-1.5">
        <div className="h-px w-9 bg-gradient-to-r from-primary/45 to-transparent" />
        <div className="h-1 w-1 rounded-full bg-primary/60 transition-transform duration-500 ease-out group-hover/teaser:scale-[1.6]" />
      </div>

      {/* Hint — descriptiv, neutral */}
      {displayHint && (
        <p className="relative text-[0.7188rem] leading-relaxed text-neutral-500 dark:text-neutral-400 max-w-[30ch]">
          {displayHint}
        </p>
      )}

      {/* CTA — Link separat ca să nu intercepteze drag-handle-ul cardului */}
      <Link
        href="/preturi"
        onClick={(e) => e.stopPropagation()}
        className="relative mt-auto pt-3 self-start cursor-pointer"
      >
        <span className="inline-flex items-center gap-1 text-[0.7rem] font-medium tracking-wide text-primary/90 border-b border-dotted border-primary/40 pb-px transition-all duration-300 group-hover/teaser:border-solid group-hover/teaser:border-primary group-hover/teaser:text-primary">
          Vezi plan
          <ArrowUpRight
            aria-hidden
            className="h-3 w-3 transition-transform duration-300 ease-out group-hover/teaser:translate-x-0.5 group-hover/teaser:-translate-y-0.5"
            strokeWidth={2}
          />
        </span>
      </Link>
    </div>
  );
}
